<template lang="pug">
div.flex.flex-col.bg-gray-200.text-black
  div.px-2.py-1.text-sm.text-black 
    span {{ name }}
    span.italic(v-if="diffData.length === 0") &nbsp;– Unchanged
  div(v-if="diffData.length > 0" class="h-[15rem] overflow-y-auto resize-y")
    div(v-for="(hunk, i) in diffData" class="bg-stone-50 font-monospace")
      .bg-gray-50.py-2
        table
          tbody
            tr.align-top.text-primary-900.bg-primary-100(v-for="line in hunk" :class="line.type === 'added' ? 'primary-green' : line.type === 'removed' ? 'primary-red' : 'primary-stone opacity-60'")
              td.select-none
                span.opacity-75.text-xs.px-1 {{ line.oldLineNumber }}
              td.select-none
                span.opacity-75.text-xs.px-1 {{ line.newLineNumber }}
              td.w-full.whitespace-pre-wrap
                .px-4.text-sm(:style="getIndentStyle(line)" v-html="line.content.trim()") 
      .bg-gray-100.ps-2.border-t.border-b.border-gray-300.h-3.flex.items-center.jsutify-center(v-if="i < diffData.length-1") 
        span.mx-auto.text-gray-500(class="[line-height:0] h-0 -my-2") ⋯ 
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { diffWords } from 'diff';

const props = defineProps<{ diff: string; name: string }>();

type DiffLine = {
  type: 'added' | 'removed' | 'unchanged';
  content: string;
  oldLineNumber: number | null;
  newLineNumber: number | null;
};

const getIndentStyle = (line: DiffLine) => {
  const leadingSpaces = line?.content?.match(/^ */)?.[0]?.length || 0;

  return leadingSpaces > 0
    ? { paddingLeft: leadingSpaces + 2 + 'ch', textIndent: '-2ch' }
    : {};
};

const diffData = computed(() => {
  const lines = props.diff.split('\n');
  const hunks: string[][] = [];
  const hunksData: DiffLine[][] = [];

  let hunkIndex = -1;

  for (const line of lines) {
    if (line.startsWith('@@')) {
      hunkIndex++;
      hunks[hunkIndex] = [];
    }

    if (hunkIndex >= 0) hunks[hunkIndex].push(line);
  }

  for (const hunk of hunks) {
    if (hunk[1].includes('No newline at end of file')) {
      // remove hunk[1]
      hunk.splice(1, 1);
    }

    let oldLineNumber = Number(
      hunk[0].match(/@@ -(\d+),\d+ \+(\d+),\d+ @@/)?.[1]
    );

    let newLineNumber = Number(
      hunk[0].match(/@@ -(\d+),\d+ \+(\d+),\d+ @@/)?.[2]
    );

    const hunkData = hunk.slice(1).map((line): DiffLine => {
      if (line.startsWith('+')) {
        return {
          type: 'added',
          content: line.slice(1),
          oldLineNumber: null,
          newLineNumber: newLineNumber++
        };
      }

      if (line.startsWith('-')) {
        return {
          type: 'removed',
          content: line.slice(1),
          oldLineNumber: oldLineNumber++,
          newLineNumber: null
        };
      }

      return {
        type: 'unchanged',
        content: line.slice(1),
        oldLineNumber: oldLineNumber++,
        newLineNumber: newLineNumber++
      };
    });

    // Highlight word diff for one-line changes
    hunkData.forEach((line, i) => {
      const a = line;
      const b = hunkData[i + 1];
      const c = hunkData[i + 2];

      const oneLineChanged =
        a.type === 'unchanged' && b?.type === 'removed' && c?.type === 'added';

      if (oneLineChanged) {
        b.content = highlightWordDiff(c.content, b.content);
        c.content = highlightWordDiff(b.content, c.content);
      }
    });

    hunksData.push(hunkData);
  }

  return hunksData;
});

function highlightWordDiff(a: string, b: string) {
  return diffWords(a, b)
    .map(({ value, added, removed }) =>
      added
        ? `<span class="bg-primary-500/25 text-primary-950 rounded py-[1px]">${value}</span>`
        : removed
          ? ''
          : value
    )
    .join('');
}
</script>
